import isNil from 'lodash-es/isNil';

import { MultibuyPriceLevel, ProductCommodityGroupAPI } from '~/api/Catalog';
import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import { calcProductOfferFields, parseProperties } from '~/types/Product';
import type { ProductProperties } from '~/types/Product/interface';
import { formatRating } from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
  isMultibuyActive,
  sortMultibuyLevels,
} from '~/utils/multibuy';

import type { Product } from './Product';

export class Offer {
  public readonly id: number;
  public readonly sku: string;
  public readonly name: string;
  public readonly description: string;
  public readonly metadataTitle: Nullable<string>;
  public readonly metadataDescription: Nullable<string>;
  public readonly ratingAverage: number;
  public readonly ratingMarksCount: number;
  public readonly priceFormatted: string;
  public readonly discountPriceFormatted: string;
  public readonly discountPercentage: string;
  public readonly pricePerUnit: string;
  public readonly previewImageThumb: string;
  public readonly multibuyPriceLevels: MultibuyPriceLevel[];
  public readonly isBackorderAvailable: boolean;
  public readonly backorderLeadTime: number;
  public readonly properties: ProductProperties;
  public readonly selectableProperties: ProductProperties;
  public readonly images: string[];
  public readonly slug: string;
  public readonly productId: number;
  public readonly categoryId: Nullable<number>;
  public readonly categoryName: Nullable<string>;
  public readonly parentCategoryId: Nullable<number>;
  public readonly sellableWarehouses: Record<string, string>;
  public readonly bundleId: Nullable<number>;
  public readonly bundleQuantity: Nullable<number>;
  public readonly productSlug: Nullable<string>;
  public readonly badges: string[];
  public readonly commodityGroup: Nullable<{
    id: number;
    name: ProductCommodityGroupAPI;
  }>;

  // TODO: make field changes using the method of this class
  public price: number;
  public discountPrice: number;
  public sellable: number;

  constructor(offer: ProductOfferAPI, product: Product) {
    const { images, imagesThumb } = parseImages(offer.images);

    const [offerProperties, selectableOfferProperties] = parseProperties(
      offer.properties || [],
    );

    this.id = offer.id || product.id;
    this.sku = offer.code;
    this.name = offer.name;
    this.price = offer.price || 0;
    this.priceFormatted = '0';
    this.discountPrice = offer.discountPrice || 0;
    this.discountPriceFormatted = '0';
    this.discountPercentage = '';
    this.pricePerUnit = '';
    this.sellable = offer.sellable || 0;
    this.description = offer.description || product.description;
    this.metadataTitle = offer.metadataTitle;
    this.metadataDescription = offer.metadataDescription;
    this.isBackorderAvailable = offer.isBackorderAvailable;
    this.backorderLeadTime = offer.backorderLeadTime;
    this.multibuyPriceLevels = sortMultibuyLevels(
      company.name === CompanyName.CityDrinks
        ? offer.multibuyPriceLevels?.length
          ? offer.multibuyPriceLevels.map((level, idx) => ({
              id: idx + 1,
              quantity: level.quantity,
              unitPrice: level.unitPrice,
            }))
          : []
        : offer.minBasketDiscountPrice && offer.minBasketDiscountQuantity
          ? [
              {
                id: 1,
                quantity: offer.minBasketDiscountQuantity,
                unitPrice: offer.minBasketDiscountPrice,
              },
            ]
          : null,
    );
    this.properties = {
      ...product.properties,
      ...offerProperties,
    };
    this.selectableProperties = selectableOfferProperties;
    this.productId = product.id;
    this.slug = product.slug;
    this.categoryId = product.categoryId || -1;
    this.categoryName = product.categoryName || '';
    this.parentCategoryId = product.parentCategoryId || -1;
    this.sellableWarehouses = offer.sellableWarehouses || {};
    this.ratingAverage = formatRating(product.ratingAverage || 0);
    this.ratingMarksCount = product.ratingMarksCount || 0;
    this.bundleId = offer.bundleId;
    this.bundleQuantity = offer.bundleQuantity || null;
    this.productSlug = offer.productSlug || null;
    this.commodityGroup = product.commodityGroup;

    this.badges = (offer.properties || [])
      .filter(({ type, value }) => type === 'badge' && value !== 'false')
      .map(({ name }) => name);

    this.images = images;
    this.previewImageThumb =
      imagesThumb[0] ||
      images[0] ||
      product.previewImageThumb ||
      product.previewImage;

    calcProductOfferFields(this);
  }

  /**
   * Возвращает наивысший применимый уровень multibuy для заданного количества.
   */
  public getApplicableMultibuyLevel(
    quantity: number,
  ): MultibuyPriceLevel | null {
    return getApplicableMultibuyLevel(this.multibuyPriceLevels, quantity);
  }

  /**
   * Возвращает минимальный уровень multibuy (с наименьшим quantity).
   */
  public getLowestMultibuyLevel(): MultibuyPriceLevel | null {
    return getLowestMultibuyLevel(this.multibuyPriceLevels);
  }

  /**
   * Проверяет, активен ли multibuy для заданного количества.
   */
  public isMultibuyActive(quantity: number): boolean {
    return isMultibuyActive(this.multibuyPriceLevels, quantity);
  }

  public static validate(offer: ProductOfferAPI, product: Product) {
    const isBundle =
      'productType' in product && product.productType === 'bundle';
    const isValidDiscount = isBundle
      ? true
      : (offer.discountPrice || 0) < (offer.price || 0);

    const hasValidChoices = !offer.properties?.some(
      (prop) =>
        prop.choices &&
        (!Array.isArray(prop.choices) ||
          prop.choices.some((choice) => choice === null)),
    );

    return (
      Boolean(offer.code) &&
      Boolean(offer.name) &&
      !isNil(offer.price) &&
      offer.price > 0 &&
      (isNil(offer.discountPrice) || offer.discountPrice > 0) &&
      isValidDiscount &&
      (!offer.multibuyPriceLevels ||
        offer.multibuyPriceLevels.length === 0 ||
        offer.multibuyPriceLevels.every(
          (level) => level.quantity > 0 && level.unitPrice > 0,
        )) &&
      hasValidChoices
    );
  }
}

const parseImages = (images: ProductOfferAPI['images']) => {
  const result = { images: [] as string[], imagesThumb: [] as string[] };

  if (!images) {
    return result;
  }

  return images.reduce((acc, { url, thumb }) => {
    if (url) {
      acc.images.push(url);
    } else if (thumb) {
      acc.imagesThumb.push(thumb);
    }

    return acc;
  }, result);
};
